import { makeAutoObservable, onBecomeObserved } from "mobx"
import { AxiosResponse } from "axios/index"

import uiStore from "@src/app/uiStore"
import {
  APIPagination,
  APIResponse,
  catchErrors,
  currenciesApi,
  merchantApi,
  withdrawalsApi,
} from "@src/shared/api"
import { WithdrawalResponse } from "@src/shared/api/generated"
import { notify } from "../components/notify"

interface FormData {
  amount?: string
  otp?: string
  symbol?: string
  whitelisted_address_id?: string
}

export type Balances = {
  available: string
  diff: string
  pending: string
  posted: string
}

export type PayoutsAvailable = {
  mass: string
  withdrawal: string
}

export type WalletItem = {
  balances: Balances
  network: string
  payouts_available: PayoutsAvailable
  symbol: string
}

export type WalletData = WalletItem[]

interface Summary {
  network?: "fiat"
  symbol?: "USD"
  totals?: {
    available: string
    diff: string
    pending: string
  }
}

type BalanceInfo = {
  available: string
  diff: string
  pending: string
  posted: string
}

export type BalanceType = {
  balances: BalanceInfo
  network: string
  payouts_available: PayoutsAvailable
  symbol: string
}
class BalancesStore {
  private _balances: WalletData = []
  private _ui = uiStore()

  private formData = {
    amount: "",
    otp: "",
    symbol: "",
    whitelisted_address_id: "",
  }

  addFormData = (value: FormData) => {
    this.formData = { ...this.formData, ...value }
  }

  chooseCurrency = (value: string) => {
    const currency = value.split(" ")
    const [symbol, network] = currency as [string, string]

    this.chosenSymbol = symbol
    this.chosenNetwork = network
  }

  chosenNetwork: string = ""

  chosenSymbol: string = ""

  createWithdrawal = async () => {
    try {
      this.isLoading = true

      const { data: result } = await withdrawalsApi.createWithdrawal({
        amount: this.formData.amount,
        id: crypto.randomUUID(),
        otp: this.formData.otp,
        symbol: this.formData.symbol,
        whitelisted_address_id: this.formData.whitelisted_address_id,
      })

      if (result.success) {
        this.withdrawDetails = result.data

        this.hasResponse = true
      } else {
        notify.error()
      }
    } catch (error: any) {
      catchErrors(error)
    } finally {
      this.isLoading = false
    }
  }

  currencies: Array<any> = []

  hasResponse = false

  isLoading = true

  loadBalances = async () => {
    this.isLoading = true

    return merchantApi
      .getBalances()
      .then((response) => {
        const { data } = response as AxiosResponse<APIResponse<BalanceType[]>>

        if (data.data) {
          this._balances = data.data as WalletData
        }
      })
      .catch(catchErrors)
      .finally(() => {
        this.isLoading = false
      })
  }
  loadConvertedBalances = async () => {
    this.isLoading = true

    return merchantApi
      .getConvertedBalances("USD", "fiat")
      .then((response) => {
        const { data } = response as AxiosResponse<APIResponse>

        this.summary = data.data.summary
      })
      .catch(catchErrors)
      .finally(() => {
        this.isLoading = false
      })
  }

  loadCurrencies = async () => {
    this.isLoading = true

    return currenciesApi
      .getCurrencies()
      .then((response) => {
        const { data } = response as AxiosResponse<APIResponse>
        this.currencies = data.data.map((d: any) => ({
          ...d,
          id: `${d.symbol}_${d.network}_${d.token_kind}_${d.decimals}`,
        }))
      })
      .catch(catchErrors)
      .finally(() => {
        this.isLoading = false
      })
  }

  pageSize = 25

  resetCurrency = () => {
    this.chosenSymbol = ""

    this.chosenNetwork = ""
  }
  resetResponse = () => (this.hasResponse = false)

  summary: Summary = {}

  total?: APIPagination["total"]
  withdrawDetails?: WithdrawalResponse

  constructor() {
    makeAutoObservable(this)

    onBecomeObserved(this, "_balances", this.loadBalances)

    onBecomeObserved(this, "currencies", this.loadCurrencies)

    if (!this._ui.isTransferioTheme) {
      onBecomeObserved(this, "summary", this.loadConvertedBalances)
    }
  }

  get balances(): WalletData {
    if (!this._balances) {
      return []
    }

    return this._balances
      .map((item, i) => ({ key: i, ...item }))
      .slice()
      .sort((a, b) => {
        const aVal = parseFloat(a.balances.available)
        const bVal = parseFloat(b.balances.available)

        if (aVal === 0 && bVal === 0) return 0

        if (aVal === 0) return 1

        if (bVal === 0) return -1

        return bVal - aVal
      })
  }

  get cards() {
    return this._balances.filter((b) => b.network === "card")
  }
}

export const balancesStore = new BalancesStore()
